import { ComponentProps, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';

import { Button } from './Button';
import { DatePicker } from './DatePicker';
import { Popover } from './Popover';
import { Select } from './Select';
import { useTranslation } from 'react-i18next';
import { Spinner } from './Spinner';
import { ChevronDownIcon } from '@treams/treams-icons';

const TagsContainer = styled(Flex)`
	> * {
		margin-right: 8px;
	}
`;

const Container = styled(Flex)<{ interactive?: boolean; plain?: boolean }>`
	${Button.Styles.SecondaryNormal}

	height: 34px;
	padding: 0px 12px;
	align-items: center;
	font-weight: bold;
	font-size: 14px;
	border-color: ${(props) => props.theme.colors.lightBlue};

	${(props) =>
		props.plain &&
		`
		padding: 0;
		border-color: transparent;
		height: auto;
		line-height: 24px;
	`}

	${(props) =>
		props.interactive &&
		`
		cursor: pointer;

		&:hover {
			background: ${props.theme.colors.whiteBlueLight};
		}
		`}
`;

const IconContainer = styled(Box)<{ color?: string }>`
	width: 16px;
	height: 16px;

	svg path {
		fill: ${(props) => props.theme.colors.slateBlue[props.color ?? 'slateBlue']};
	}
`;

const Status = styled(Box)<{ $color: string }>`
	width: 10px;
	height: 10px;
	border-radius: 5px;
	background: ${(props) => props.theme.colors[props.$color]};
`;

const Label = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
`;

const Value = styled(Box)`
	color: black;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: pre;
`;

export function Tags(props: PropsWithChildren<BoxProps>) {
	return <TagsContainer {...props} />;
}

export function _TagsItem({
	icon,
	status,
	label,
	value,
	onClick,
	plain,
	...rest
}: BoxProps & {
	icon?: ReactNode;
	status?: string;
	label?: string;
	value: string;
	plain?: boolean;
	onClick?: () => void;
}) {
	return (
		<Container {...rest} interactive={!!onClick} onClick={onClick} plain={plain}>
			{label && (
				<Label flexGrow={0} flexShrink={0} mr={1}>
					{label}:
				</Label>
			)}
			{status && <Status flexGrow={0} flexShrink={0} $color={status} mr={1} />}
			<Value flexShrink={1} flexGrow={0}>
				{value}
			</Value>
			{icon && <IconContainer ml={1}>{icon}</IconContainer>}
		</Container>
	);
}

export function _TagsItemSkeleton({ label, ...rest }: { label: string }) {
	return (
		<Container {...rest}>
			{label && (
				<Label flexGrow={0} flexShrink={0} mr={1}>
					{label}:
				</Label>
			)}
			<Value flexShrink={1} flexGrow={0} ml={2}>
				<Spinner />
			</Value>
		</Container>
	);
}

_TagsItem.Skeleton = _TagsItemSkeleton;
Tags.Item = _TagsItem;

function ItemPopover(
	props: BoxProps & {
		icon?: ReactNode;
		label?: string;
		color?: string;
		title: string;
		fetch?: () => void;
		contents: ReactNode;
		visible?: boolean;
		onClick?: () => void;
		onDismiss: () => void;
	},
) {
	return (
		<Popover.Target
			visible={props.visible ?? false}
			contents={props.contents}
			onDismiss={props.onDismiss}
		>
			<Tags.Item
				icon={props.icon}
				label={props.label}
				value={props.title}
				status={props.color}
				onClick={props.onClick}
			/>
		</Popover.Target>
	);
}

export type ItemSelectProps = ComponentProps<typeof ItemSelect>;

function ItemSelect(
	props: BoxProps & {
		enabled?: boolean;
		autoFocus?: boolean;
		search?: boolean;
		label?: string;
		options?: {
			icon?: ReactNode;
			value?: string;
			title: string;
			color?: string;
			context?: ReactNode;
		}[];
		selectedOption?: {
			icon?: ReactNode;
			value?: string;
			title: string;
			color?: string;
			context?: ReactNode;
		};
		selectedValue?: string;
		onClick?: () => void;
		onChange: (value?: string) => void;
		fetch?: () => void;
	},
) {
	const [visible, setVisible] = useState(props.autoFocus ?? false);
	const { t } = useTranslation();

	useEffect(() => {
		if (visible) {
			props.fetch?.();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible]);

	const option =
		props.selectedOption ?? props.options?.find((option) => option.value === props.selectedValue);

	return (
		<ItemPopover
			icon={option?.icon ?? <ChevronDownIcon />}
			label={props.label}
			title={option?.title ?? t('tags.item.option.none')}
			color={option?.color}
			fetch={props.fetch}
			visible={visible && props.enabled !== false}
			onClick={props.enabled !== false ? () => setVisible(true) : props.onClick}
			onDismiss={() => setVisible(false)}
			contents={
				<Select.Menu
					search={props.search}
					selectedValue={props.selectedValue ?? props.selectedOption?.value}
					options={props.options}
					onChange={(value) => {
						props.onChange(value);
						setVisible(false);
					}}
				/>
			}
		/>
	);
}

_TagsItem.Select = ItemSelect;

function ItemDatePicker(
	props: BoxProps & {
		label?: string;
		value?: Date;
		onChange: (value?: Date) => void;
		fetch?: () => void;
	},
) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (visible) {
			props.fetch?.();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible]);

	return (
		<ItemPopover
			label={props.label}
			title={props.value ? moment(props.value).format('D MMM YYYY') : t('datePicker.value.empty')}
			fetch={props.fetch}
			visible={visible}
			onClick={() => setVisible(true)}
			onDismiss={() => setVisible(false)}
			contents={
				<DatePicker
					value={props.value}
					onChange={(newValue) => props.onChange(newValue)}
					onDismiss={() => setVisible(false)}
				/>
			}
		/>
	);
}

_TagsItem.DatePicker = ItemDatePicker;
